// PropertyConnect Analytics Phase 1 — lightweight, first-party, fire-and-forget tracker.
// Never blocks navigation/UX. One taxonomy shared with the backend registry.
import { BACKEND } from "@/lib/api";

const COLLECT_URL = `${BACKEND}/api/analytics/collect`;
const VID_KEY = "pc_av";        // anonymous visitor id (persistent, localStorage)
const SESS_KEY = "pc_sess";     // { id, ts } session (sessionStorage, 30-min sliding)
const FT_KEY = "pc_ft";         // first-touch attribution (persistent)
const SESSION_MS = 30 * 60 * 1000;

const uuid = () =>
  (crypto?.randomUUID?.() || `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`).replace(/-/g, "");

function getVisitorId() {
  try {
    let v = localStorage.getItem(VID_KEY);
    if (!v) { v = "av_" + uuid(); localStorage.setItem(VID_KEY, v); }
    return v;
  } catch { return "av_ephemeral"; }
}

// 30-minute sliding inactivity window. Returns { id, isNew }.
function getSession() {
  try {
    const now = Date.now();
    const raw = sessionStorage.getItem(SESS_KEY);
    let s = raw ? JSON.parse(raw) : null;
    let isNew = false;
    if (!s || !s.id || now - (s.ts || 0) > SESSION_MS) { s = { id: "s_" + uuid() }; isNew = true; }
    s.ts = now;
    sessionStorage.setItem(SESS_KEY, JSON.stringify(s));
    return { id: s.id, isNew };
  } catch { return { id: "s_ephemeral", isNew: false }; }
}

// Capture first-touch attribution ONCE per anonymous visitor; never overwritten.
function firstTouch() {
  try {
    const existing = localStorage.getItem(FT_KEY);
    if (existing) return JSON.parse(existing);
    const q = new URLSearchParams(window.location.search);
    const ft = {
      source: q.get("utm_source") || "",
      medium: q.get("utm_medium") || "",
      campaign: q.get("utm_campaign") || "",
      referrer: document.referrer || "",
    };
    localStorage.setItem(FT_KEY, JSON.stringify(ft));
    return ft;
  } catch { return { source: "", medium: "", campaign: "", referrer: "" }; }
}

// Short-lived client dedupe so React rerenders/StrictMode don't inflate counts.
const _recent = new Map();
function dupe(key) {
  const now = Date.now();
  const last = _recent.get(key);
  _recent.set(key, now);
  if (_recent.size > 200) for (const [k, t] of _recent) if (now - t > 5000) _recent.delete(k);
  return last && now - last < 1200;
}

function send(payload) {
  try {
    const body = JSON.stringify(payload);
    if (navigator.sendBeacon) {
      navigator.sendBeacon(COLLECT_URL, new Blob([body], { type: "application/json" }));
      return;
    }
  } catch { /* fall through */ }
  try {
    const t = localStorage.getItem("pc_token");
    fetch(COLLECT_URL, {
      method: "POST", keepalive: true,
      headers: { "Content-Type": "application/json", ...(t ? { Authorization: `Bearer ${t}` } : {}) },
      body: JSON.stringify(payload),
    }).catch(() => {});
  } catch { /* never throw into UX */ }
}

// Public: emit a taxonomy event. opts: {path, entity_type, entity_id, metadata}
export function trackEvent(event_name, opts = {}) {
  try {
    const path = (opts.path || window.location.pathname || "").split("?")[0];
    if (dupe(`${event_name}|${path}|${opts.entity_id || ""}`)) return;
    const ft = firstTouch();
    const { id: session_id } = getSession();
    send({
      event_name,
      anonymous_visitor_id: getVisitorId(),
      session_id,
      path,
      entity_type: opts.entity_type || null,
      entity_id: opts.entity_id || null,
      source: ft.source || null,
      medium: ft.medium || null,
      campaign: ft.campaign || null,
      referrer: ft.referrer || null,
      metadata: opts.metadata || null,
    });
  } catch { /* fire-and-forget */ }
}

// Fire once per SPA route change (dedupe handles rerenders). Emits session_started for a new session.
export function trackPageView(path) {
  const { isNew } = getSession();
  if (isNew) trackEvent("session_started", { path });
  trackEvent("page_view", { path });
}
